import {
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  Ref,
  useCallback,
} from 'react';
import {
  SupplementalKnowledgeAiReply,
  SupplementalKnowledgeValueMessage,
  SupplementKnowledgeDetailResponse,
  SupplementKnowledgeValueMessage,
  TestSupplementalKnowledgeData,
} from 'services/api/supplementKnowledge';
import { BuyerQuestionList } from '../Detail/BuyerQuestionList';
import { BuyerQuestion } from '../Detail/BuyerQuestion';
import { Divider } from 'antd';
import { AIReply } from '../Detail/AIReply';

interface QAInfoProps {
  onlyQuestion?: boolean;
  conversation: Conversation.Conversation;
  testData?: TestSupplementalKnowledgeData;
  dataSource: SupplementKnowledgeDetailResponse;
  onRefresh: () => void;
}

export interface QAInfoRef {
  scrollToTop: () => void;
}

const message2AIReply = (
  messages: SupplementKnowledgeValueMessage[],
): SupplementalKnowledgeAiReply => {
  const replyContent = messages.map((message) => ({
    message: message.message,
    msgType: message.msgType,
    messageId: message.messageId,
    translateMap: message.translateMap,
  }));
  return {
    replyContent,
    replyTime: 0,
    testReqId: '',
    citeKnowledge: [],
    sourceKnowledge: [],
    req: '',
  };
};

const QAInfoCore = (props: QAInfoProps, ref: Ref<QAInfoRef>) => {
  const { onlyQuestion, conversation, testData, dataSource, onRefresh } = props;
  const {
    id: knowledgeId,
    supplementalKnowledgeValue,
    aiReply: sourceAIReply,
    aiReplyMap: sourceAIReplyMap,
  } = dataSource;
  const { originQuestions, buyerNick, botQaMap } = supplementalKnowledgeValue;

  const aiReplyRef = useRef<HTMLDivElement>(null);
  const [aiReplyFlashKey, setAiReplyFlashKey] = useState(0);

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (aiReplyRef.current) {
        aiReplyRef.current.scrollIntoView({
          block: 'center',
          inline: 'center',
          behavior: 'smooth',
        });
        setAiReplyFlashKey((key) => key + 1);
      }
    },
  }));

  const getAiReply = useCallback(
    (msgId?: string) => {
      if (testData) {
        return testData;
      }
      if (sourceAIReply.req) {
        return sourceAIReply;
      }
      if (msgId && botQaMap && botQaMap[msgId]) {
        const qa = botQaMap[msgId];
        return message2AIReply(qa);
      }
    },
    [testData, sourceAIReply, botQaMap],
  );

  const getAiReplyMap = useCallback(
    (originQuestions: SupplementalKnowledgeValueMessage[]) => {
      const map: Record<string, SupplementalKnowledgeAiReply> = {};
      originQuestions.forEach((question) => {
        const msgId = question.messageId;
        const fromSourceAIReply = sourceAIReplyMap?.[msgId];
        if (fromSourceAIReply && fromSourceAIReply.req) {
          map[msgId] = sourceAIReplyMap[msgId];
        } else if (msgId && botQaMap && botQaMap[msgId]) {
          const qa = botQaMap[msgId];
          map[msgId] = message2AIReply(qa);
        }
      });
      return map;
    },
    [botQaMap, sourceAIReplyMap],
  );

  const showBuyerQuestionList = useMemo(() => {
    if (testData?.testReqId) {
      return false;
    }

    const { testReqId: detailTestReqId } = dataSource;

    return !detailTestReqId && originQuestions && originQuestions.length > 1;
  }, [dataSource, originQuestions, testData?.testReqId]);

  if (showBuyerQuestionList) {
    const aiReplyMap = originQuestions ? getAiReplyMap(originQuestions) : undefined;
    return (
      <BuyerQuestionList
        originQuestions={originQuestions}
        buyerNick={buyerNick}
        aiReplyMap={aiReplyMap}
      />
    );
  } else {
    const question = originQuestions?.[0];
    const aiReply = getAiReply(question?.messageId);

    return (
      <>
        <BuyerQuestion
          conversation={conversation}
          supplementalKnowledgeValue={supplementalKnowledgeValue}
          knowledgeId={knowledgeId}
          showKnowledgeSelector
          onRefresh={onRefresh}
        />
        {aiReply && !onlyQuestion && (
          <>
            <Divider dashed className="!my-4" />
            <div ref={aiReplyRef}>
              <AIReply aiReply={aiReply} locate={true} flashKey={aiReplyFlashKey} />
            </div>
          </>
        )}
      </>
    );
  }
};

export const QAInfo = forwardRef(QAInfoCore);
